import {
  Card, CircularProgress, TableCell, TableRow
} from '@material-ui/core';
import { Catalogue } from 'api/query/catalogue/catalogue';
import CatalogueApi from 'api/query/catalogue/catalogue-api';
import { CataloguesResponse } from 'api/query/catalogue/catalogue-response';
import { CatalogueSort } from 'api/query/catalogue/catalogue-sort';
import { CatalogueSortBy } from 'api/query/catalogue/catalogue-sort-by.enum';
import { CatalogueSortDir } from 'api/query/catalogue/catalogue-sort-dir.enum';
import { CatalogueType } from 'api/query/catalogue/catalogue-type.enum';
import Axios, { CancelTokenSource } from 'axios';
import Accordion from 'components/accordion';
import Table from 'components/table';
import { Column } from 'components/table/column';
import LoadMore from 'components/table/load-more';
import TableFade from 'components/table/table-fade';
import deepEqual from 'deep-equal';
import { ResultsNumConstants } from 'pages/search/results/results-view/results-num-constants.enum';
import React, { useCallback, useEffect, useState } from 'react';
import { useBreakpoints } from 'utils/use-breakpoints';
import { Catalogues } from '../../catalogue-constants.enum';
import { CatalogueLink } from '../../catalogue-link/catalogue-link';
import {
  displaySaleType, formatDate, getTableHeaders, NoCataloguesMessage
} from '../../catalogue-utils';
import { CatalogueWrapper } from '../../catalogue-wrapper';
import { NoCatalogues } from '../../no-catalogues/no-catalogues';
import CatalogueCard from './catalogue-card';
import { SalesCataloguesProps } from './sales-catalogues-props';
import './sales-catalogues.scss';

/**
 * List of sales catalogues.
 * @param props
 */
export const SalesCatalogues: React.FC<SalesCataloguesProps> = (props) => {
  const [currentData, setCurrentData] = useState<CataloguesResponse>({ totalCount: 0, catalogues: [] });
  const [pastData, setPastData] = useState<CataloguesResponse>({ totalCount: 0, catalogues: [] });
  const [currentColumns, setCurrentColumns] = useState<Column[]>([]);
  const [pastColumns, setPastColumns] = useState<Column[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [currentSort, setCurrentSort] = useState<CatalogueSort>({ sortBy: CatalogueSortBy.SaleDate, sortDir: CatalogueSortDir.asc });
  const [pastSort, setPastSort] = useState<CatalogueSort>({ sortBy: CatalogueSortBy.SaleDate, sortDir: CatalogueSortDir.asc });
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const { phone } = useBreakpoints();

  /**
   * Calls api with selected attributes to get catalogues
   */
  const loadResults = useCallback(async (current: boolean, sorting: CatalogueSort, source: CancelTokenSource, pageNo?: number) => CatalogueApi.getCatalogueList(CatalogueType.Sales, sorting, props.db, source.token, current, pageNo), [props.db]);

  /**
   * Load more current catalogues if possible.
   */
  const loadMore = async () => {
    const source = Axios.CancelToken.source();
    setLoadingMore(true);

    const newPageNumber = pageNumber + 1;
    setPageNumber(newPageNumber);

    const response = await loadResults(true, currentSort, source, newPageNumber);

    setTimeout(() => {
      const d = {
        ...response,
        catalogues: currentData.catalogues.concat(response.catalogues),
      };

      setLoadingMore(false);
      setCurrentData(d);
    }, ResultsNumConstants.Delay);
  };

  /**
   * Sorts selected catalogues
   * @param sortBy sortBy
   * @param sortDir sorting direction
   * @param isCurrent is selected header from current sales catalogues
   */
  const sortResult = async (sortBy: CatalogueSortBy, sortDir: CatalogueSortDir, isCurrent: boolean) => {
    const source = Axios.CancelToken.source();
    setLoading(true);

    const sortOption: CatalogueSort = {
      sortBy,
      sortDir,
    };

    const d = await loadResults(isCurrent, sortOption, source, 0);

    setTimeout(() => {
      if (isCurrent) {
        setCurrentData(d);
        setCurrentSort(sortOption);
      } else {
        setPastData(d);
        setPastSort(sortOption);
      }

      setLoading(false);
    }, ResultsNumConstants.Delay);
  };

  /**
   * Initial current catalogue retrieve
   */
  useEffect(() => {
    const source = Axios.CancelToken.source();
    setLoading(true);

    const initialSort = {
      sortBy: CatalogueSortBy.SaleDate,
      sortDir: CatalogueSortDir.asc,
    };

    const getCatalogues = async () => {
      const d = await loadResults(true, initialSort, source, 0);
      setCurrentData(d);
      setLoading(false);
    };
    getCatalogues();

    return () => {
      source.cancel();
    };
  }, [loadResults]);

  /**
   * Initial past catalogue retrieve
   */
  useEffect(() => {
    const source = Axios.CancelToken.source();
    setLoading(true);

    const initialSort = {
      sortBy: CatalogueSortBy.SaleDate,
      sortDir: CatalogueSortDir.asc,
    };

    const getCatalogues = async () => {
      const d = await loadResults(false, initialSort, source, 0);
      setPastData(d);
    };
    getCatalogues();

    return () => {
      source.cancel();
    };
  }, [loadResults]);

  /**
   * Sets columns from retrieved current catalogues
   */
  useEffect(() => {
    if (currentData.catalogues.length) {
      const cols = getTableHeaders(Catalogues.Sales, currentSort.sortBy, currentSort.sortDir);
      if (!deepEqual(currentColumns, cols)) {
        setCurrentColumns(cols);
      }
    }
  }, [currentColumns, currentData, currentSort]);

  /**
   * Sets columns from retrieved past catalogues
   */
  useEffect(() => {
    if (pastData.catalogues.length) {
      const cols = getTableHeaders(Catalogues.Sales, pastSort.sortBy, pastSort.sortDir);
      if (!deepEqual(pastColumns, cols)) {
        setPastColumns(cols);
      }
    }
  }, [pastColumns, pastData, pastSort]);

  return (
    <CatalogueWrapper
      catalogue={Catalogues.Sales}
      totalCount={currentData.totalCount}
      resultsCount={currentData.catalogues.length}
      className="sg-sales-catalogues"
    >
      {loading ? (<div className="sg-catalogues__indicator"><CircularProgress color="primary" /></div>) : (
        <>
          {currentData.catalogues.length
            ? (
              <>
                {phone
                  ? (
                    <div>
                      {currentData.catalogues.map((catalogue: Catalogue, index: number) => (
                        <TableFade
                          in
                          customDelay={(index % 20) * 50}
                          key={catalogue.id}
                        >
                          <CatalogueCard
                            catalogue={catalogue}
                            db={props.db}
                            user={props.user}
                            trait={props.trait}
                            type={CatalogueType.Sales}
                          />
                        </TableFade>
                      ))}
                      <LoadMore
                        loading={loadingMore!}
                        loadedResults={currentData.catalogues.length!}
                        totalCount={currentData.totalCount!}
                        onLoadMore={loadMore}
                        loadedNoun="catalogues"
                      />
                    </div>
                  )
                  : (
                    <Table
                      columns={currentColumns}
                      isVerticallyScrolled
                      scrollShadow
                      stickyHeader
                      totalCount={currentData.totalCount}
                      resultsCount={currentData.catalogues.length}
                      loading={loading}
                      loadingMore={loadingMore}
                      enableSort
                      sortBy="id"
                      onHeaderSelected={(col, dir) => sortResult(col, dir, true)}
                      onLoadMore={loadMore}
                      loadedNoun="catalogues"
                    >
                      {currentData.catalogues.map((catalogue: Catalogue, index: number) => (
                        <TableFade
                          in
                          customDelay={(index % 20) * 50}
                          key={catalogue.id}
                        >
                          <Card
                            component={TableRow}

                            className="sg-table__row"
                          >
                            <TableCell className="cell sticky small" />
                            <TableCell className="cell sg-sales-catalogues__title">
                              <CatalogueLink
                                db={props.db}
                                user={props.user}
                                trait={props.trait}
                                label={catalogue.title}
                                id={catalogue.id}
                                type={CatalogueType.Sales}
                                className="wrap-and-truncate"
                              />
                            </TableCell>
                            <TableCell
                              className="cell sg-sales-catalogues__date"
                              dangerouslySetInnerHTML={{ __html: formatDate(catalogue.saleStartDateDesc, catalogue.saleEndDateDesc) }}
                            />
                            <TableCell className="cell sg-sales-catalogues__listed">{catalogue.listedAnimals}</TableCell>
                            <TableCell className="cell sg-sales-catalogues__flock">{catalogue.flock}</TableCell>
                            <TableCell className="cell sg-sales-catalogues__location"><div className="wrap-and-truncate">{catalogue.city} {catalogue.state}</div></TableCell>
                            <TableCell className="cell sg-sales-catalogues__type">{displaySaleType(catalogue.saleTypeDesc)}</TableCell>
                            <TableCell className="cell sticky small" />
                          </Card>
                        </TableFade>
                      ))}
                    </Table>
                  )}
              </>
            )
            : <NoCatalogues mainMessage={NoCataloguesMessage.NoSalesCataloguesTitle} />}
        </>
      )}
      {
        !loading && (
          <div className="sg-sales-catalogues__past">
            <Accordion
              title="Past sales"
              enableFullScreenStickyHeader
              enableFullScreenHeaderBack
            >
              <div className="sg-sales-catalogues__past__count">{pastData.catalogues.length} sales catalogues</div>
              {pastData.catalogues.length ? (
                <>
                  {phone ? pastData.catalogues.map((catalogue: Catalogue, index: number) => (
                    <TableFade
                      in
                      customDelay={(index % 20) * 50}
                      key={catalogue.id}
                    >
                      <CatalogueCard
                        catalogue={catalogue}
                        db={props.db}
                        user={props.user}
                        trait={props.trait}
                        type={CatalogueType.Sales}
                      />
                    </TableFade>
                  ))
                    : (
                      <Table
                        columns={pastColumns}
                        isVerticallyScrolled
                        scrollShadow
                        stickyHeader
                        totalCount={pastData.totalCount}
                        resultsCount={pastData.catalogues.length}
                        loading={false}
                        loadingMore={false}
                        enableSort
                        onHeaderSelected={(col, dir) => sortResult(col, dir, false)}
                        showLoadMore={false}
                        showHeader
                        className="sg-sales-catalogues"
                        sortBy="id"
                        loadedNoun="catalogues"
                      >
                        {pastData.catalogues.map((catalogue: Catalogue, index: number) => (
                          <TableFade
                            in
                            customDelay={(index % 20) * 50}
                            key={catalogue.id}
                          >
                            <Card
                              component={TableRow}
                              className="sg-table__row"
                            >
                              <TableCell className="cell sticky small" />
                              <TableCell className="cell sg-sales-catalogues__title">
                                <CatalogueLink
                                  db={props.db}
                                  user={props.user}
                                  trait={props.trait}
                                  label={catalogue.title}
                                  id={catalogue.id}
                                  type={CatalogueType.Sales}
                                  className="wrap-and-truncate"
                                />
                              </TableCell>
                              <TableCell
                                className="cell sg-sales-catalogues__date"
                                dangerouslySetInnerHTML={{ __html: formatDate(catalogue.saleStartDateDesc, catalogue.saleEndDateDesc) }}
                              />
                              <TableCell className="cell sg-sales-catalogues__listed">{catalogue.listedAnimals}</TableCell>
                              <TableCell className="cell sg-sales-catalogues__flock">{catalogue.flock}</TableCell>
                              <TableCell className="cell sg-sales-catalogues__location"><div className="wrap-and-truncate">{catalogue.city} {catalogue.state}</div></TableCell>
                              <TableCell className="cell sg-sales-catalogues__type">{displaySaleType(catalogue.saleTypeDesc)}</TableCell>
                              <TableCell className="cell sticky small" />
                            </Card>
                          </TableFade>
                        ))}
                      </Table>
                    )}
                </>
              ) : <NoCatalogues mainMessage={NoCataloguesMessage.NoSalesCataloguesTitle} />}
            </Accordion>
          </div>
        )
      }
    </CatalogueWrapper>
  );
};

(SalesCatalogues as any).whyDidYouRender = true;
